import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  FileCheck2,
  ShieldCheck,
  MapPin,
  Scale,
  Workflow,
  Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Container, SectionHeading, Eyebrow } from "@/components/common/Section";
import Reveal from "@/components/common/Reveal";
import CtaBand from "@/components/common/CtaBand";
import LicaraMock from "@/components/common/LicaraMock";
import { fetchContent } from "@/lib/apiClient";
import { SOLUTIONS } from "@/data/solutions";

const FALLBACK = {
  hero_eyebrow: "Sentira · Tech solutions & development",
  hero_title: "Enterprise software for",
  hero_title_highlight: "modern public operations",
  hero_subtitle:
    "Sentira builds mission-critical systems for agencies and enterprises. Our flagship platform, Licara, unifies permits, enforcement, and case management in one secure hub.",
};

const pillars = [
  {
    icon: FileCheck2,
    title: "Permits & Licensing",
    desc: "Digitize applications, approvals, renewals, and inspections with a full audit trail.",
  },
  {
    icon: MapPin,
    title: "Field Ops & Citations",
    desc: "Mobile citation capture, geolocation, and evidence — with offline sync for the field.",
  },
  {
    icon: Scale,
    title: "Case Management",
    desc: "Track enforcement cases from intake to resolution with deadlines and escalations.",
  },
];

const steps = [
  { label: "Permit", desc: "Intake & approvals" },
  { label: "Inspection", desc: "Scheduled checks" },
  { label: "Citation", desc: "Violation issued" },
  { label: "Case", desc: "Resolution & audit" },
];

const trust = [
  { value: "1 platform", label: "Replaces spreadsheets & legacy tools" },
  { value: "70%", label: "Less manual paperwork" },
  { value: "Weeks", label: "To go-live, not months" },
  { value: "24/7", label: "Secure cloud availability" },
];

export default function Home() {
  const [c, setC] = useState(FALLBACK);

  useEffect(() => {
    fetchContent()
      .then((d) => setC({ ...FALLBACK, ...d }))
      .catch(() => setC(FALLBACK));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="hero-glow pointer-events-none absolute inset-0" />
        <div className="bg-sentira-grid bg-radial-fade pointer-events-none absolute inset-0 opacity-70" />
        <Container className="relative py-24 md:py-32">
          <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <Reveal>
                <Eyebrow>{c.hero_eyebrow}</Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-5 text-4xl font-semibold leading-[1.05] tracking-[-0.02em] sm:text-5xl lg:text-6xl">
                  {c.hero_title}{" "}
                  <span className="text-gradient-teal">{c.hero_title_highlight}</span>
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
                  {c.hero_subtitle}
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                  <Button
                    asChild
                    size="lg"
                    className="bg-primary text-primary-foreground shadow-[0_0_0_1px_rgba(20,184,166,0.25),0_10px_30px_rgba(20,184,166,0.12)] transition-transform hover:-translate-y-0.5 hover:bg-[hsl(173_80%_34%)]"
                  >
                    <Link to="/demo" data-testid="hero-primary-cta-button">
                      Request a demo
                      <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="secondary">
                    <Link to="/solutions/licara" data-testid="hero-secondary-cta-button">
                      Explore Licara
                    </Link>
                  </Button>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" /> Government & municipalities
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-primary" /> Enterprise-ready
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Workflow className="h-4 w-4 text-primary" /> Workflow automation
                  </span>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.15} y={24}>
              <LicaraMock />
            </Reveal>
          </div>
        </Container>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-border bg-[hsl(220_35%_4%)]">
        <Container className="py-12">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            {trust.map((t, i) => (
              <Reveal key={t.label} delay={i * 0.05}>
                <div>
                  <p className="text-2xl font-semibold text-foreground md:text-3xl">{t.value}</p>
                  <p className="mt-1.5 text-sm text-muted-foreground">{t.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* PURPOSE-BUILT POSITIONING + FLAGSHIP */}
      <section className="py-20 md:py-28">
        <Container>
          <div className="grid items-center gap-12 lg:grid-cols-[1fr_1fr]">
            <div>
              <Eyebrow>Purpose-built solution specialists</Eyebrow>
              <h2 className="mt-4 text-3xl font-semibold leading-tight tracking-[-0.02em] sm:text-4xl">
                We don't ship templates. We build for the mission.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                Sentira designs and engineers purpose-built software for
                agencies and enterprises — solutions shaped around your exact
                workflows, security needs, and outcomes. Our flagship product,
                Licara, is proof of what that focus delivers.
              </p>
              <ul className="mt-7 space-y-3">
                {[
                  "Built for the mission — not forced from a template",
                  "Secure & compliant by design, from day one",
                  "A long-term partner, from launch through every iteration",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            {SOLUTIONS.map((s) => (
              <Reveal key={s.id} y={24}>
                <Link
                  to={s.path}
                  data-testid={`home-solution-${s.id}`}
                  className="group flex h-full flex-col rounded-[24px] border border-border bg-card p-8 shadow-[var(--shadow-card)] transition-all hover:-translate-y-1 hover:border-[rgba(125,227,218,0.35)]"
                >
                  <div className="flex items-center justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-secondary">
                      <s.icon className="h-6 w-6 text-primary" />
                    </span>
                    <span className="rounded-full border border-border bg-secondary px-3 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                      {s.badge}
                    </span>
                  </div>
                  <h3 className="mt-6 text-2xl font-semibold tracking-[-0.01em]">{s.name}</h3>
                  <p className="mt-1 text-sm font-medium text-primary">{s.tagline}</p>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-muted-foreground">{s.description}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
                    Explore {s.name}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* PILLARS */}
      <section className="pb-20 md:pb-28">
        <Container>
          <SectionHeading
            eyebrow="Inside Licara — our flagship"
            title="One hub for the entire enforcement lifecycle"
            description="Permits, field operations, and case management — unified in a single, secure system of record."
          />
          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="group h-full rounded-[var(--radius-card)] border border-border bg-card p-7 shadow-[var(--shadow-card)] transition-colors hover:border-[rgba(125,227,218,0.35)]">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-secondary">
                    <p.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* WORKFLOW */}
      <section className="pb-20 md:pb-28">
        <Container>
          <div className="rounded-[24px] border border-border bg-[hsl(220_30%_8%)] p-8 md:p-14">
            <SectionHeading
              eyebrow="How it flows"
              title="From intake to resolution — connected end to end"
              align="center"
            />
            <div className="mt-14 grid gap-4 md:grid-cols-4">
              {steps.map((s, i) => (
                <Reveal key={s.label} delay={i * 0.08}>
                  <div className="relative rounded-xl border border-border bg-card p-5">
                    <span className="font-mono text-xs text-primary">0{i + 1}</span>
                    <h4 className="mt-2 text-base font-semibold">{s.label}</h4>
                    <p className="mt-1 text-sm text-muted-foreground">{s.desc}</p>
                    {i < steps.length - 1 && (
                      <ArrowRight className="absolute -right-3 top-1/2 hidden h-5 w-5 -translate-y-1/2 text-primary/50 md:block" />
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <CtaBand />
    </div>
  );
}
